<template>
  <!-- prettier-ignore -->
  <div class="pw-wrapper">
    <div
      :class="pwClasses"
      :data-product-id="productId">
      <ovp-widget-loading v-if="isLoading" />

      <div class="pw-tabs">
        <template v-for="tab in tabs">
          <div
            :key="tab.id"
            data-track-as="pricewidget-tabs"
            :data-track-text="tab.id"
            class="pw-tabs__tab"
            :class="{ 'pw-tabs__tab--active': activeTab.id === tab.id }"
            @click="select(tab)">
            <span v-text="$t(tab.title)" />
            <ovp-icon
              art="light"
              name="angle-down"
            />
          </div>
          <template v-if="!$browser.isDesktop">
            <div
              :key="`container-${tab.id}`"
              class="pw-content">
              <component
                :is="tab.component"
                v-show="activeTab.id === tab.id"
                :product-id="productId"
                :source="source"
              />
            </div>
          </template>
        </template>
      </div>

      <template v-if="$browser.isDesktop">
        <div class="pw-content">
          <template v-for="tab in tabs">
            <component
              :is="tab.component"
              v-show="activeTab.id === tab.id"
              :key="tab.id"
              :product-id="productId"
              :source="source"
            />
          </template>
        </div>
      </template>
    </div>
    <ovp-message
    v-if="showMessage"
    class="pw-message"
    :text="$t('temp.navigation.disabled')"
  />
  </div>
</template>

<script>
import Vue from '@/setup'

import OvpWidgetLoading from '@/components/Container/WidgetLoading'

import { STATE_LOADING, STATE_READY } from '@/config/constants'

import { EVENT_READY, SOURCE_PRICE_WIDGET } from '@/config/events'
import { mapState } from 'vuex'
import OvpMessage from '@/components/Container/Message'

export default Vue.extend({
  name: 'PriceWidget',

  components: {
    OvpMessage,
    OvpWidgetLoading,
  },

  props: {
    productId: {
      type: String,
      required: true,
    },

    source: {
      type: String,
      default: SOURCE_PRICE_WIDGET,
    },
  },

  data() {
    return {
      activeTab: false,
      state: STATE_LOADING,
    }
  },

  created() {
    this.$notification.setTranslator((key, params) => this.$t(key, params))

    this.$EventBus.$once(EVENT_READY, () => {
      this.state = STATE_READY
    })
  },

  mounted() {
    if (!this.activeTab) {
      this.select(this.tabs[0], false)
    }
  },

  computed: {
    ...mapState('basket', ['collective', 'existingCustomer']),
    isLoading() {
      return this.state === STATE_LOADING
    },

    pwClasses() {
      return {
        pw: true,
        'pw-blur': this.showMessage,
      }
    },

    showMessage() {
      return !this.isLoading && !!this.existingCustomer
    },

    tabs() {
      const tabEntries = [
        {
          component: () => import('@/modules/PriceWidget/components/Assistant'),
          id: 'assistant',
          order: 2,
          title: 'screen.priceWidget.assistant.title',
        },
        {
          component: () => import('@/modules/PriceWidget/components/Widget'),
          id: 'widget',
          order: 0,
          title: 'screen.priceWidget.widget.title',
        },
      ]

      // FLEX-1373: Hide Alva in FAB on SCPC collective entry
      if (this.collective?.channel !== 'SCPC') {
        tabEntries.push({
          component: () => import('@/modules/PriceWidget/components/Aide'),
          id: 'aide',
          order: 1,
          title: 'screen.priceWidget.aide.title',
        })
      }
      return tabEntries.sort((a, b) => (a.order > b.order ? 1 : -1))
    },
  },

  methods: {
    /**
     * select tab
     *
     * @param {Object} tab   Tab
     * @param {Boolean} scroll   scroll tab into view? default: true
     */
    select(tab, scroll = true) {
      this.activeTab = tab

      if (scroll) {
        const el = this.$el.querySelector(`.pw-tabs__tab[data-track-text='${tab.id}']`)
        el?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    },
  },
})
</script>

<style name="mobile" scoped>
.pw-wrapper {
  position: relative;
}

.pw-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  border: 1px solid var(--c-light-gray);
}

.pw-blur {
  filter: blur(16px);
  pointer-events: none;
}
.pw {
  &-tabs {
    display: flex;
    flex-direction: column;

    &__tab {
      padding: 10px 0;
      border: 1px solid var(--c-black);
      text-align: center;
      transition: all 0.3s ease-in-out;
      margin: 0 0 10px 0;
      position: relative;

      .icon {
        font-size: 16px;
        position: absolute;
        top: 15px;
        right: 20px;
      }

      &--active {
        background-color: var(--c-original-green);
        border-color: var(--c-original-green);
        color: var(--c-white);
        font-weight: bold;
        position: relative;
        margin: 0 0 0 0;

        &:after {
          content: '';
          position: absolute;
          bottom: -11px;
          left: calc(50% - 5px);
          z-index: 1;

          width: 0px;
          height: 0px;
          border-right: 10px solid transparent;
          border-left: 10px solid transparent;
          border-top: 10px solid var(--c-original-green);
        }
      }
    }
  }

  &-content {
    margin: 0 0 20px 0;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .pw {
    position: relative;
    display: grid;
    grid-template-columns: 240px 1fr;

    &-tabs {
      display: grid;
      grid-gap: 15px;
      grid-template-rows: repeat(auto-fill, 40px);

      &__tab {
        height: 40px;
        border: 1px solid var(--c-black);
        width: 220px;
        padding: 10px 0;
        text-align: center;
        cursor: pointer;
        font-size: 15px;
        line-height: 20px;
        transition: all 0.3s ease-in-out;
        margin: 0;

        .icon {
          display: none;
        }

        &:hover {
          background-color: var(--c-original-green);
          color: var(--c-white);
        }

        &--active {
          background-color: var(--c-original-green);
          border-color: var(--c-original-green);
          color: var(--c-white);
          font-weight: bold;
          position: relative;

          &:after {
            content: '';
            position: absolute;
            bottom: initial;
            left: initial;
            top: 8px;
            right: -10px;

            width: 0px;
            height: 0px;
            border-right: 0;
            border-top: 10px solid transparent;
            border-bottom: 10px solid transparent;
            border-left: 10px solid var(--c-original-green);
          }
        }
      }
    }

    &-content {
    }
  }
}
</style>
